import { useEffect, useId, useState } from 'react';
import { Form } from 'react-bootstrap';
import axiosInstance from 'utils/axiosInterceptors';

type CarMakeOption = {
  carMakeId: number;
  name: string;
};

export function CarMakeSelect({
  name = 'CarMake',
  value,
  onChange
}: {
  name?: string;
  value?: string;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => void;
}) {
  const listId = useId();
  const [makes, setMakes] = useState<CarMakeOption[]>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get('/api/v1/carMakes?page=1&pageSize=999&orderBy=name')
      .then(response => {
        if (cancelled) return;
        const data = response.data.data || response.data;
        setMakes(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setMakes([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const current = value ?? '';
  const query = current.trim().toLowerCase();
  const seen = new Set<string>();
  const suggestions = makes.flatMap(make => {
    const makeName = make.name.trim();
    const key = makeName.toLowerCase();
    if (!makeName || seen.has(key)) return [];
    if (query && !key.includes(query)) return [];
    seen.add(key);
    return [{ carMakeId: make.carMakeId, name: makeName }];
  });

  const emit = (next: string) => {
    onChange({
      target: { name, value: next }
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const choose = (makeName: string) => {
    emit(makeName);
    setOpen(false);
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label>
        Make <span className="text-danger">*</span>
      </Form.Label>
      <div className="position-relative">
        <Form.Control
          type="text"
          name={name}
          value={current}
          required
          autoComplete="off"
          role="combobox"
          aria-label="Make"
          aria-autocomplete="list"
          aria-expanded={open && suggestions.length > 0}
          aria-controls={listId}
          placeholder="Search or enter make"
          onChange={event => {
            onChange(event);
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => {
            setOpen(true);
            setHighlight(0);
          }}
          onBlur={() => setOpen(false)}
          onKeyDown={event => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
              setHighlight(index => Math.min(index + 1, Math.max(suggestions.length - 1, 0)));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setHighlight(index => Math.max(index - 1, 0));
            } else if (event.key === 'Enter' && open && suggestions[highlight]) {
              event.preventDefault();
              choose(suggestions[highlight].name);
            } else if (event.key === 'Escape') {
              setOpen(false);
            }
          }}
        />
        {open && suggestions.length > 0 && (
          <div
            id={listId}
            role="listbox"
            className="position-absolute w-100 bg-body border rounded shadow-sm mt-1"
            style={{ zIndex: 5, maxHeight: 220, overflowY: 'auto' }}
          >
            {suggestions.map((make, index) => (
              <button
                key={make.carMakeId}
                type="button"
                role="option"
                aria-selected={index === highlight}
                className={`dropdown-item${index === highlight ? ' active' : ''}`}
                onMouseDown={event => {
                  event.preventDefault();
                  choose(make.name);
                }}
                onMouseEnter={() => setHighlight(index)}
              >
                {make.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </Form.Group>
  );
}
